import CheckboxGroup from "@shared/components/ui-kit/form/CheckboxGroup/CheckboxGroup.web";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import InputDatePicker from "@shared/components/ui-kit/form/InputDatePicker/InputDatePicker.web";
import { enums } from "@shared/constants";
import { useTranslation } from "@shared/utils/internationalization";
import Checkbox from "@ui-kit/form/Checkbox/Checkbox.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import FormRow from "@ui-kit/form/FormRow/FormRow.web";
import Input from "@ui-kit/form/Input/Input.web";
import NumericFormat from "@ui-kit/form/NumericFormat/NumericFormat.web";
import Select from "@ui-kit/form/Select/Select.web";
import Textarea from "@ui-kit/form/Textarea/Textarea.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { formatDateToISODateString } from "@shared/utils/dates/formatting";
import { tryParseJson } from "@shared/utils/data/data-utils";
import { GuestQuestionProps } from "./GuestQuestion.props";
import { useGuestQuestion } from "./useGuestQuestion";

export default function GuestQuestion(props: GuestQuestionProps) {
  const { t } = useTranslation("common");
  const {
    title,
    help_text,
    type,
    required,
    maxlength,
    options,
    onUpdateQuestion,
    placeholder,
  } = props;

  const formContext = useFormContext();

  const {
    inputId,
    selectOptions,
    hasCheckboxSelection,
    updateDropdownSelection,
  } = useGuestQuestion({ ...props, form: formContext });

  const requiredFieldText = t("This field is required.");
  const requiredNumberText = t(
    "This field is required. Only numeric values are allowed."
  );
  const requiredCheckboxText = t("At least one checkbox must be selected.");
  const formErrors = formContext.formState.errors;

  const getInputElement = useCallback(() => {
    switch (type) {
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.text.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredFieldText : false,
            }}
            render={({ field }) => (
              <Input
                {...field}
                isInvalid={Boolean(formErrors[inputId])}
                id={`${inputId}-text-input`}
                data-testid={`${inputId}-text-input`}
                type="text"
                placeholder={placeholder ?? t("Your response")}
                maxLength={
                  maxlength ??
                  enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.text.max_response_length
                }
                onBlur={() => onUpdateQuestion(inputId)}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.paragraph.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredFieldText : false,
            }}
            render={({ field }) => (
              <Textarea
                {...field}
                isInvalid={Boolean(formErrors[inputId])}
                id={`${inputId}-textarea-input`}
                data-testid={`${inputId}-textarea-input`}
                placeholder={placeholder ?? t("Your message")}
                maxLength={
                  maxlength ??
                  enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.paragraph
                    .max_response_length
                }
                maxHeight="200px"
                onBlur={() => onUpdateQuestion(inputId)}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.select.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredFieldText : false,
              validate: required
                ? (value) => {
                    // Ensure a valid option is selected, not just the placeholder
                    const isValidSelection = selectOptions?.some(
                      (option) => option.name === value
                    );
                    return isValidSelection || requiredFieldText;
                  }
                : undefined,
            }}
            render={({ field }) => (
              <Select
                {...field}
                isInvalid={Boolean(formErrors[inputId])}
                id={`${inputId}-select-input`}
                data-testid={`${inputId}-select-input`}
                items={selectOptions ?? []}
                placeholder={placeholder ?? t("Please select an option")}
                selectedItem={
                  selectOptions?.find(
                    (q) => q.name === formContext.getValues(inputId)
                  ) ?? { id: -1, name: "" }
                }
                onSelectedItemChange={({ selectedItem }) => {
                  const newValue =
                    selectedItem?.name && selectedItem.id !== -1
                      ? selectedItem.name
                      : "";
                  field.onChange(newValue);
                  updateDropdownSelection(selectedItem?.name);
                }}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.decimal.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredNumberText : false,
            }}
            render={({ field }) => (
              <NumericFormat
                {...field}
                id={`${inputId}-decimal-input`}
                data-testid={`${inputId}-decimal-input`}
                placeholder={placeholder ?? t("Enter a decimal number")}
                decimalScale={
                  (maxlength ??
                    enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.decimal
                      .max_response_length) - 1
                }
                customInput={Input}
                allowNegative
                maxLength={
                  maxlength ??
                  enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.decimal
                    .max_response_length
                }
                onBlur={() => onUpdateQuestion(inputId)}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.int.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredNumberText : false,
            }}
            render={({ field }) => (
              <NumericFormat
                {...field}
                id={`${inputId}-integer-input`}
                data-testid={`${inputId}-integer-input`}
                placeholder={placeholder ?? t("Enter a number")}
                decimalScale={0}
                customInput={Input}
                allowNegative
                maxLength={
                  maxlength ??
                  enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.int.max_response_length
                }
                onBlur={() => onUpdateQuestion(inputId)}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.char_int.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredNumberText : false,
            }}
            render={({ field }) => (
              <NumericFormat
                {...field}
                id={`${inputId}-char-integer-input`}
                data-testid={`${inputId}-char-integer-input`}
                placeholder={placeholder ?? t("Enter a number")}
                decimalScale={0}
                customInput={Input}
                allowNegative
                maxLength={
                  maxlength ??
                  enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.char_int
                    .max_response_length
                }
                onBlur={() => onUpdateQuestion(inputId)}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.date.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            rules={{
              required: required ? requiredFieldText : false,
              validate: required
                ? (value) => {
                    // Ensure a valid option is selected, not just the placeholder
                    const isValidDate = !!value;
                    return isValidDate || requiredFieldText;
                  }
                : undefined,
            }}
            render={({ field }) => (
              <InputDatePicker
                {...field}
                captionLayout="dropdown"
                mode="single"
                startMonth={new Date(new Date().getFullYear() - 100, 0)} // 100 years ago
                endMonth={new Date(new Date().getFullYear() + 100, 11)} // 100 years from now
                id={`${inputId}-date-input`}
                data-testid={`${inputId}-date-input`}
                onChangeSelected={({ selected }) => {
                  const { onChange } = field;
                  if (selected) {
                    let selectedDate: Date | undefined;
                    if (Array.isArray(selected) && selected.length === 1) {
                      [selectedDate] = selected;
                    } else if (selected instanceof Date) {
                      selectedDate = selected;
                    }

                    if (selectedDate) {
                      const formattedDate =
                        formatDateToISODateString(selectedDate);
                      onChange(formattedDate);
                    } else {
                      onChange(selectedDate);
                    }
                  } else {
                    // Handle when date is cleared/removed
                    onChange("");
                  }
                  formContext.trigger(inputId);
                  onUpdateQuestion(inputId);
                }}
              />
            )}
          />
        );
      case enums.DYN_FORM_INPUT_TYPE_OPTIONS_MAP.checkbox_group.id:
        return (
          <Controller
            name={inputId}
            control={formContext.control}
            defaultValue={[]}
            rules={{
              required:
                required && !hasCheckboxSelection
                  ? requiredCheckboxText
                  : false,
              validate: (value) => {
                if (required) {
                  const hasSelection = value && Object.keys(value).length > 0;
                  return hasSelection || requiredCheckboxText;
                }
                return true;
              },
            }}
            render={({ field }) => {
              const value =
                typeof field.value === "string"
                  ? tryParseJson(field.value)
                  : field.value;

              const selectedValues = value
                ? Object.keys(value).filter((key) => value[key])
                : [];

              return (
                <CheckboxGroup
                  value={selectedValues}
                  onChange={(values) => {
                    // Convert array format back to object format
                    if (values.length === 0) {
                      field.onChange(null);
                    } else {
                      const objectValue = values.reduce((acc, value) => {
                        acc[value] = true;
                        return acc;
                      }, {} as { [key: string]: boolean });
                      field.onChange(objectValue);
                    }
                    onUpdateQuestion(inputId);
                  }}
                >
                  <Flex direction="column" gap="xs">
                    {options?.map((opt) => (
                      <Checkbox
                        key={opt.text}
                        value={opt.text}
                        id={`${inputId}-checkbox-input-${opt.text}`}
                        data-testid={`${inputId}-checkbox-input-${opt.text}`}
                        width="fit-content"
                      >
                        {opt.text}
                      </Checkbox>
                    ))}
                  </Flex>
                </CheckboxGroup>
              );
            }}
          />
        );
      default:
        return <></>;
    }
  }, [type, maxlength, options, required, inputId, formErrors]);

  return (
    <Flex direction="column" gap="2xs">
      <FormRow>
        <FormControl
          isInvalid={
            Boolean(formContext.formState.errors[inputId]?.message) &&
            formContext.getFieldState(inputId).invalid &&
            formContext.getFieldState(inputId).isDirty
          }
        >
          <FormLabel htmlFor={inputId}>
            <Text fontSize="sm" color="font.dark">
              {title}
            </Text>
            <Text fontSize="xs" color="gray.600" mb="xs">
              {help_text}
            </Text>
          </FormLabel>
          {getInputElement()}
          <FormErrorMessage>
            {String(formContext.formState.errors[inputId]?.message)}
          </FormErrorMessage>
        </FormControl>
      </FormRow>
    </Flex>
  );
}
